import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  FileText,
  Image,
  Video,
  File,
  ChevronRight,
  MessageSquare,
  RefreshCw,
  Filter,
  Check,
  X,
  RotateCcw,
  Calendar,
  User,
  Paperclip,
  ExternalLink
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const statusConfig = {
  pending: { 
    label: 'Pending Review', 
    color: 'amber', 
    icon: Clock,
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    border: 'border-amber-200'
  },
  approved: { 
    label: 'Approved', 
    color: 'green', 
    icon: CheckCircle2,
    bg: 'bg-green-100',
    text: 'text-green-700',
    border: 'border-green-200'
  },
  rejected: { 
    label: 'Rejected', 
    color: 'red', 
    icon: XCircle,
    bg: 'bg-red-100',
    text: 'text-red-700',
    border: 'border-red-200'
  },
  revision_requested: { 
    label: 'Revisions Requested', 
    color: 'purple', 
    icon: RotateCcw,
    bg: 'bg-purple-100',
    text: 'text-purple-700',
    border: 'border-purple-200'
  }
};

const typeConfig = {
  report: { label: 'Report', icon: FileText, color: 'blue' },
  creative: { label: 'Creative', icon: Image, color: 'pink' },
  strategy: { label: 'Strategy', icon: FileText, color: 'purple' },
  content: { label: 'Content', icon: FileText, color: 'green' },
  budget: { label: 'Budget', icon: FileText, color: 'amber' },
  other: { label: 'Other', icon: File, color: 'slate' }
};

const priorityConfig = {
  low: { label: 'Low', color: 'slate' },
  normal: { label: 'Normal', color: 'blue' },
  high: { label: 'High', color: 'amber' },
  urgent: { label: 'Urgent', color: 'red' }
};

export default function ClientApprovalsView({ client }) {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, completed
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [responseComment, setResponseComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchApprovals();
  }, []);

  const fetchApprovals = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/client/approvals`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setApprovals(data.approvals || []);
      }
    } catch (err) {
      console.error('Error fetching approvals:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = async (status) => {
    if (!selectedApproval) return;
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/client/approvals/${selectedApproval.approval_id}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status, comment: responseComment })
      });
      
      if (res.ok) {
        setShowModal(false);
        setSelectedApproval(null);
        setResponseComment('');
        fetchApprovals();
      }
    } catch (err) {
      console.error('Error responding to approval:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredApprovals = approvals.filter(a => {
    if (filter === 'pending') return a.status === 'pending';
    if (filter === 'completed') return a.status !== 'pending';
    return true;
  });

  const pendingCount = approvals.filter(a => a.status === 'pending').length;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-approvals-view">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Approvals</h2>
          <p className="text-slate-500">Review and approve items submitted by your team</p>
        </div>
        {pendingCount > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-xl">
            <Clock className="w-5 h-5 text-amber-600" />
            <span className="font-medium text-amber-700">{pendingCount} pending review</span>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {[
          { id: 'all', label: 'All' },
          { id: 'pending', label: 'Pending' },
          { id: 'completed', label: 'Completed' }
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              filter === f.id
                ? 'bg-blue-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Approvals List */}
      {filteredApprovals.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <CheckCircle2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">
            {filter === 'pending' ? 'No pending approvals' : 'No approvals yet'}
          </h3>
          <p className="text-slate-500">
            {filter === 'pending' 
              ? 'All items have been reviewed!' 
              : 'Items submitted for your approval will appear here'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredApprovals.map(approval => {
            const status = statusConfig[approval.status] || statusConfig.pending;
            const type = typeConfig[approval.approval_type] || typeConfig.other;
            const priority = priorityConfig[approval.priority] || priorityConfig.normal;
            const StatusIcon = status.icon;
            const TypeIcon = type.icon;
            
            return (
              <motion.div
                key={approval.approval_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white rounded-2xl border-2 ${
                  approval.status === 'pending' ? 'border-amber-200' : 'border-slate-200'
                } p-5 hover:shadow-lg transition-shadow cursor-pointer`}
                onClick={() => { setSelectedApproval(approval); setShowModal(true); }}
              >
                <div className="flex items-start gap-4">
                  {/* Type Icon */}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    type.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                    type.color === 'pink' ? 'bg-pink-100 text-pink-600' :
                    type.color === 'purple' ? 'bg-purple-100 text-purple-600' :
                    type.color === 'green' ? 'bg-green-100 text-green-600' :
                    type.color === 'amber' ? 'bg-amber-100 text-amber-600' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    <TypeIcon className="w-6 h-6" />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-slate-800 text-lg">{approval.title}</h3>
                        <p className="text-slate-500 text-sm mt-0.5 line-clamp-2">{approval.description}</p>
                      </div>
                      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${status.bg} ${status.text} text-sm font-medium flex-shrink-0`}>
                        <StatusIcon className="w-4 h-4" />
                        {status.label}
                      </div>
                    </div>
                    
                    {/* Meta info */}
                    <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-slate-500">
                      <span className="flex items-center gap-1.5">
                        <User className="w-4 h-4" />
                        {approval.submitted_by_name}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        {formatDate(approval.submitted_at)}
                      </span>
                      {approval.attachments?.length > 0 && (
                        <span className="flex items-center gap-1.5">
                          <Paperclip className="w-4 h-4" />
                          {approval.attachments.length} attachment{approval.attachments.length !== 1 ? 's' : ''}
                        </span>
                      )}
                      {approval.priority !== 'normal' && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          priority.color === 'amber' ? 'bg-amber-100 text-amber-700' :
                          priority.color === 'red' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {priority.label} Priority
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Approval Detail Modal */}
      <AnimatePresence>
        {showModal && selectedApproval && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
            >
              {/* Modal Header */}
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        statusConfig[selectedApproval.status]?.bg
                      } ${statusConfig[selectedApproval.status]?.text}`}>
                        {statusConfig[selectedApproval.status]?.label}
                      </span>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600`}>
                        {typeConfig[selectedApproval.approval_type]?.label}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-800">{selectedApproval.title}</h3>
                  </div>
                  <button
                    onClick={() => setShowModal(false)}
                    className="p-2 hover:bg-slate-100 rounded-lg text-slate-400"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Description */}
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Description</h4>
                  <p className="text-slate-600 whitespace-pre-wrap">{selectedApproval.description}</p>
                </div>

                {/* Meta */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-1">Submitted By</h4>
                    <p className="text-slate-600">{selectedApproval.submitted_by_name}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-1">Submitted On</h4>
                    <p className="text-slate-600">{formatDateTime(selectedApproval.submitted_at)}</p>
                  </div>
                  {selectedApproval.due_date && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 mb-1">Due Date</h4>
                      <p className="text-slate-600">{formatDate(selectedApproval.due_date)}</p>
                    </div>
                  )}
                </div>

                {/* Attachments */}
                {selectedApproval.attachments?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">Attachments</h4>
                    <div className="space-y-2">
                      {selectedApproval.attachments.map((att, idx) => (
                        <a
                          key={idx}
                          href={att.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                        >
                          <File className="w-5 h-5 text-slate-400" />
                          <span className="flex-1 text-slate-700 font-medium">{att.name}</span>
                          <ExternalLink className="w-4 h-4 text-slate-400" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Previous Response */}
                {selectedApproval.response_comment && selectedApproval.status !== 'pending' && (
                  <div className={`p-4 rounded-xl ${statusConfig[selectedApproval.status]?.bg} ${statusConfig[selectedApproval.status]?.border} border`}>
                    <h4 className={`text-sm font-semibold ${statusConfig[selectedApproval.status]?.text} mb-1`}>
                      Your Response
                    </h4>
                    <p className="text-slate-700">{selectedApproval.response_comment}</p>
                    <p className="text-xs text-slate-500 mt-2">
                      {formatDateTime(selectedApproval.responded_at)}
                    </p>
                  </div>
                )}

                {/* Response Form - Only for pending */}
                {selectedApproval.status === 'pending' && (
                  <div className="border-t border-slate-200 pt-6">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">Your Response</h4>
                    <textarea
                      value={responseComment}
                      onChange={e => setResponseComment(e.target.value)}
                      placeholder="Add a comment (optional)..."
                      rows={3}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none"
                    />
                  </div>
                )}
              </div>

              {/* Modal Footer - Action Buttons */}
              {selectedApproval.status === 'pending' && (
                <div className="p-4 border-t border-slate-200 bg-slate-50">
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleResponse('revision_requested')}
                      disabled={submitting}
                      className="flex-1 px-4 py-3 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Request Revisions
                    </button>
                    <button
                      onClick={() => handleResponse('rejected')}
                      disabled={submitting}
                      className="flex-1 px-4 py-3 bg-red-100 hover:bg-red-200 text-red-700 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
                    >
                      <X className="w-4 h-4" />
                      Reject
                    </button>
                    <button
                      onClick={() => handleResponse('approved')}
                      disabled={submitting}
                      className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
                    >
                      {submitting ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                      Approve
                    </button>
                  </div>
                </div>
              )}

              {/* Close button for completed approvals */}
              {selectedApproval.status !== 'pending' && (
                <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end">
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-6 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-medium"
                  >
                    Close
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
